import * as analytics from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import {
  GooglePickerAPIScriptNotLoadedError,
  validateAndDownloadGoogleDriveFiles,
} from 'notebook/notes/previews/redirect-oriented-google-drive-integration/google-drive-download/ValidateAndDownloadGoogleDriveFiles';

function getDownloadToastMessage(numberOfFilesDownloaded: number, totalNumberOfFiles: number): string {
  const fileLabel = totalNumberOfFiles === 1 ? 'file' : 'files';
  return `Downloaded ${numberOfFilesDownloaded} out of ${totalNumberOfFiles} ${fileLabel} from Google Drive...`;
}

export interface CreateUploadDropdownGDrivePickerHandlersDeps {
  setIsFlowActive: (active: boolean) => void;
  onGoogleDriveFilesReady: (files: Array<File>) => void;
  fileId: string;
}

/**
 * Callbacks passed to the raw Google Drive Picker (via buildAndShowGoogleDrivePicker).
 * Extracted for unit testing. Unlike the callback-oriented flow, the redirect-oriented
 * flow obtains the OAuth access token from the Benchling popup up front, so handlePicked
 * receives the token from the caller rather than fetching one via GIS.
 */
export function createUploadDropdownGDrivePickerHandlers(
  deps: CreateUploadDropdownGDrivePickerHandlersDeps
): {
  handleCanceled: () => void;
  handlePicked: (docs: Array<google.picker.DocumentObject>, accessToken: string) => Promise<void>;
} {
  const {setIsFlowActive, onGoogleDriveFilesReady, fileId} = deps;

  const handleCanceled = (): void => {
    analytics.trackEvent(ELN_ANALYTICS_ACTIONS.GOOGLE_DRIVE_PICKER_CANCELED, {
      fileId,
    });
    setIsFlowActive(false);
  };

  // TODO(BNCH-152463): We are collecting all files in an array from the async generator validateAndDownloadGoogleDriveFiles, then passing
  // that array to onGoogleDriveFilesReady. This defeats the purpose of using an async generator to reduce the amount of File blobs stored in
  // memory at once.
  //
  // To fix this, refactor NotePartGrid's file upload logic so it can upload a file one-at-a-time rather than needing the entire array
  // of files. Then, change the usage of onGoogleDriveFilesReady here so we pass in each file one at a time to it, rather than collecting
  // them in an array first.

  const handlePicked = async (
    docs: Array<google.picker.DocumentObject>,
    accessToken: string
  ): Promise<void> => {
    setIsFlowActive(false);
    if (docs.length === 0) {
      return;
    }

    analytics.trackEvent(ELN_ANALYTICS_ACTIONS.GOOGLE_DRIVE_PICKER_FILES_SELECTED, {
      fileId,
      file_count: docs.length,
    });

    let downloadedFileCount = 0;
    const downloadToastId = ToastActions.pushToast({
      type: ToastType.INFO,
      message: getDownloadToastMessage(downloadedFileCount, docs.length),
      duration: 0,
    });

    try {
      const files: Array<File> = [];
      for await (const downloadResult of validateAndDownloadGoogleDriveFiles(docs, accessToken)) {
        const {file, error} = downloadResult;
        if (error != null) {
          ToastActions.pushToast({
            type: ToastType.DANGER,
            message: error.userDescription,
            duration: 0,
          });
        } else if (file != null) {
          files.push(file);
          downloadedFileCount += 1;
          ToastActions.pushToast({
            id: downloadToastId,
            type: ToastType.INFO,
            message: getDownloadToastMessage(downloadedFileCount, docs.length),
            duration: 0,
          });
        }
      }

      if (files.length > 0) {
        onGoogleDriveFilesReady(files);
      }
    } catch (err) {
      if (!(err instanceof GooglePickerAPIScriptNotLoadedError)) {
        throw err;
      }
      ToastActions.pushToast({
        type: ToastType.WARNING,
        message:
          'Google Drive integration not loaded. Please click on "Upload from Google Drive" and go through the authentication and file selection process again',
      });
    } finally {
      // Convert the persistent (duration 0) progress toast into a self-dismissing
      // one showing how many files were actually downloaded. This runs in `finally`
      // so the persistent toast is always cleared — on success, on the handled
      // script-not-loaded warning, AND when an unexpected error is re-thrown above.
      // Without this in `finally`, a re-thrown error would leave the duration-0
      // toast stuck on screen indefinitely with a stale count.
      ToastActions.pushToast({
        id: downloadToastId,
        type: ToastType.INFO,
        message: getDownloadToastMessage(downloadedFileCount, docs.length),
        duration: 5,
      });
    }
  };

  return {handleCanceled, handlePicked};
}
